import { ENotAuthNavigation } from '@hts/shared/enums';
import { IMappedSubmenuCategory } from '@hts/shared/types';
import { HtsLink, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { throttle } from '@hts/shared/utils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useImage from 'use-image';
import styles from '../../styles/Header/HoverSubmenus.module.scss';

interface IHoverSubmenusProps {
  submenu?: IMappedSubmenuCategory;
  isOpen: boolean;
  containerClassName?: string;
  onCloseSubmenu: () => void;
}

export const HoverSubmenus: React.FC<IHoverSubmenusProps> = memo(
  ({ submenu, isOpen, containerClassName = '', onCloseSubmenu }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [activeImageSrc, setActiveImageSrc] = useState('');
    const [alignRight, setAlignRight] = useState(false);
    const [image] = useImage(activeImageSrc);

    const aspectRatio = image ? image.width / image.height : 1;

    const containerStyles = useMemo<React.CSSProperties>(() => {
      const styles: React.CSSProperties = {
        visibility: isOpen ? 'visible' : 'hidden',
        opacity: isOpen ? 1 : 0,
      };

      if (alignRight) styles.right = 0;
      else styles.left = 0;

      return styles;
    }, [alignRight, isOpen]);

    useEffect(() => {
      setActiveImageSrc(submenu?.fileUrl || '');

      return () => {
        setActiveImageSrc('');
      };
    }, [submenu, isOpen]);

    useEffect(() => {
      const handleResize = () => {
        if (containerRef.current && submenu) {
          const rect = containerRef.current.getBoundingClientRect();
          const windowWidth = window.innerWidth;

          if (rect.right > windowWidth) {
            setAlignRight(true);
          } else {
            setAlignRight(false);
          }
        }
      };

      const throttledHandleResize = throttle(handleResize, 200);

      window.addEventListener('resize', throttledHandleResize);

      handleResize();

      return () => {
        window.removeEventListener('resize', throttledHandleResize);
      };
    }, [submenu]);

    const onMouseEnter = useCallback(
      (mediaPath: string | null) => () => {
        setActiveImageSrc(mediaPath || '');
      },
      []
    );

    const renderSubmenu = useCallback(
      ({ id, name, categories, fileUrl, slug }: IMappedSubmenuCategory, index: number) => (
        <li key={`${id}-${index}`} onMouseEnter={onMouseEnter(fileUrl || '')}>
          <HtsLink
            label={name}
            redirectTo={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${submenu?.slug}?sub=${slug}`}
            typographyProps={{ variant: 'caption', fontWeight: '400' }}
            linkProps={{ onClick: onCloseSubmenu }}
          />
        </li>
      ),
      [submenu?.slug, onCloseSubmenu]
    );

    return (
      <div
        ref={containerRef}
        className={`${styles.container} ${containerClassName}`}
        style={containerStyles}
      >
        <div className={styles.sumbenusContainer}>
          <HtsLink
            label={submenu?.name || ''}
            redirectTo={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${submenu?.slug}`}
            linkProps={{
              onMouseEnter: onMouseEnter(submenu?.fileUrl || ''),
              onClick: onCloseSubmenu,
            }}
            typographyProps={{ variant: 'body2', fontWeight: '500' }}
          />

          {Boolean(submenu?.categories.length) && <ul>{submenu?.categories.map(renderSubmenu)}</ul>}
        </div>

        <div className={styles.imageContainer}>
          {activeImageSrc && <img src={activeImageSrc} alt="Img" style={{ aspectRatio }} />}

          <div className={styles.seeAllContainer}>
            <Link
              to={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${submenu?.slug}`}
              className={styles.seeAllLink}
              onClick={onCloseSubmenu}
            >
              <HtsTypography fontWeight="400">See all</HtsTypography>

              <HtslIcon icon="arrow-right" />
            </Link>
          </div>
        </div>
      </div>
    );
  }
);
